import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  clearThemePreviewGuidStorage,
  getThemePreviewGuidFromStorage,
  syncThemePreviewSearchParam,
} from "./themePreviewStorage";

const previewBarCss = `
.theme-preview-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10002;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  min-height: 44px;
  box-sizing: border-box;
  background-color: #121212;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.35);
  border-top: 1px solid #333333;
  transition: min-height 0.2s ease, padding 0.2s ease, gap 0.2s ease;
}
.theme-preview-bar--minimized {
  min-height: 16px;
  height: 16px;
  padding: 0 8px;
  gap: 0;
  justify-content: center;
  overflow: hidden;
}
.theme-preview-bar--minimized .theme-preview-bar__left,
.theme-preview-bar--minimized .theme-preview-bar__right {
  display: none;
}
.theme-preview-bar__left {
  flex: 1;
  min-width: 0;
  word-break: break-all;
}
.theme-preview-bar__right {
  flex: 1;
  display: flex;
  justify-content: flex-end;
}
.theme-preview-bar__toggle {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  padding: 4px 8px;
  margin: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  line-height: 1;
  transition: background-color 0.15s ease;
}
.theme-preview-bar__toggle:hover {
  background: rgba(255,255,255,0.12);
}
.theme-preview-bar--minimized .theme-preview-bar__toggle {
  padding: 0 6px;
  font-size: 10px;
}
.theme-preview-bar__btn {
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  background: #333333;
  color: #ffffff;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.15s ease;
}
.theme-preview-bar__btn:hover {
  background: #4a4a4a;
}
.theme-preview-bar__btn:active {
  background: #3d3d3d;
}
.theme-preview-bar__btn--copy {
  padding: 6px 10px;
  font-size: 11px;
  margin-left: 10px;
}
.theme-preview-bar__btn--exit {
  padding: 6px 14px;
  font-size: 13px;
}
`;

export default function ThemePreviewBar() {
  const history = useHistory();
  const location = useLocation();
  const [minimized, setMinimized] = useState(false);
  const [themePreviewGuid, setThemePreviewGuid] = useState(() =>
    getThemePreviewGuidFromStorage()
  );

  useEffect(() => {
    const sync = () => setThemePreviewGuid(getThemePreviewGuidFromStorage());
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  useEffect(() => {
    syncThemePreviewSearchParam(history, location, themePreviewGuid);
  }, [history, location, themePreviewGuid]);

  if (!themePreviewGuid) {
    return null;
  }

  const exitPreview = () => {
    clearThemePreviewGuidStorage();
    const url = new URL(window.location.href);
    url.searchParams.delete("preview");
    const next = url.pathname + (url.search || "") + url.hash;
    window.location.replace(next);
  };

  return (
    <div
      className={
        "theme-preview-bar" +
        (minimized ? " theme-preview-bar--minimized" : "")
      }
      role="status"
      aria-live="polite"
    >
      <style>{previewBarCss}</style>
      <div className="theme-preview-bar__left">
        <strong>Previewing</strong>: {themePreviewGuid}
        <button
          type="button"
          className="theme-preview-bar__btn theme-preview-bar__btn--copy"
          title="Copy preview id"
          onClick={() => navigator.clipboard.writeText(themePreviewGuid)}
        >
          <i className="fas fa-copy" />
        </button>
      </div>
      <button
        type="button"
        className="theme-preview-bar__toggle"
        title={minimized ? "Expand preview bar" : "Minimize preview bar"}
        aria-expanded={!minimized}
        onClick={() => setMinimized((m) => !m)}
      >
        <i
          className={
            minimized ? "fas fa-chevron-up" : "fas fa-chevron-down"
          }
        />
      </button>
      <div className="theme-preview-bar__right">
        <button
          type="button"
          className="theme-preview-bar__btn theme-preview-bar__btn--exit"
          onClick={exitPreview}
        >
          Exit Preview
        </button>
      </div>
    </div>
  );
}
